import React, { useState } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Button, CardContent, CardActions, Card, TextField, Box, Stack, FormControlLabel, Checkbox, Grid, Typography, IconButton, Modal, Chip, Divider, Container, LinearProgress, InputAdornment } from '@mui/material';
import { Post, apiURL } from '../../../utils/apiCalls';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Base64File } from '../../../utils/Base64File';
import { dataURItoBlob } from '../../../utils/Base64File';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import KYCService, { KYC_STATUS } from '../../../utils/kycService';
import { handleKYCError, handleKYCSuccess } from '../../../utils/kycUtils';
import Logo from '../../../layouts/dashboard/Logo';

// Styled Components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 1200,
  margin: '0 auto',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));


// Validation Schema
const ProcurementSchema = Yup.object().shape({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  email: Yup.string().email('Invalid Email').required('Email is required'),
  password: Yup.string().required('Password is required').min(7, 'Password must be at least 7 characters'),
  phone: Yup.string().matches(/^[6-9]\d{9}$/, 'Please Enter valid Phone Number').required('Phone Number is required'),
  aadhar_number: Yup.string().matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhar number').required('Aadhar Number is required'),
  pan_number: Yup.string().required('Pan Number is required'),
  address: Yup.string().required('Personal Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  pincode: Yup.string().required('Pincode is required'),
  bank_name: Yup.string().required('Bank Name is required'),
  account_number: Yup.string().required('Account Number is required'),
  bank_ifsc_code: Yup.string().required('IFSC Number is required'),
  bank_address: Yup.string().required('Bank Address is required'),
  active: Yup.string().required('Active is required'),
  // KYC Fields
  pan_name: Yup.string(),
  pan_date_of_birth: Yup.date(),
  aadhaar_name: Yup.string(),
  bank_account_holder_name: Yup.string()
});

const ProcurementCreate = () => {
  const { qr } = useParams();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [upload, setUpload] = useState({
    aadhar_upload: '',
    pan_upload: ''
  });
  const [kycStatus, setKycStatus] = useState({
    pan: null,
    aadhaar: null,
    bank: null
  });
  const [kycLoading, setKycLoading] = useState({
    pan: false,
    aadhaar: false,
    bank: false
  });
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationType, setVerificationType] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false); // Modal state for OTP
  const [otpType, setOtpType] = useState(''); // OTP type (phone or email)
  const [otpValue, setOtpValue] = useState(''); // OTP value entered by user
  const [isEmailVerified, setIsEmailVerified] = useState(false); // Email verification status
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // Phone verification status

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      password: '',
      email: '',
      phone: '',
      aadhar_number: '',
      pan_number: '',
      aadhar_upload: {},
      pan_upload: {},
      address: '',
      bank_name: '',
      account_number: '',
      bank_ifsc_code: '',
      bank_address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      nominee_relation: '',
      nominee_age: '',
      nominee_name: '',
      active: 'yes',
      // KYC specific fields
      pan_name: '',
      pan_date_of_birth: '',
      aadhaar_name: '',
      bank_account_holder_name: '',
      pan_verified: false,
      aadhaar_verified: false,
      bank_verified: false
    },
    validationSchema: ProcurementSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
      // Check if both OTPs are verified before submitting
      if (!isEmailVerified && !isPhoneVerified) {
        setFieldError('email', 'Please verify Email OTP');
        setFieldError('phone', 'Please verify Phone OTP');
        setFieldTouched('email', true);
        setFieldTouched('phone', true);
        setSubmitting(false);
        return;
      } else if (!isEmailVerified) {
        setFieldError('email', 'Please verify Email OTP');
        setFieldTouched('email', true);
        setSubmitting(false);
        return;
      } else if (!isPhoneVerified) {
        setFieldError('phone', 'Please verify Phone OTP');
        setFieldTouched('phone', true);
        setSubmitting(false);
        return;
      }
      
      // Check if all KYC verifications are complete
      if (!values.pan_verified || !values.aadhaar_verified || !values.bank_verified) {
        toast.error('Please complete all KYC verifications before submitting');
        setSubmitting(false);
        return;
      }

      const response = await Post(apiURL + '/api/v1/users/create/admin/without_auth', { ...values, qr,role:3 });
      if (response && response.data && response.data.status) {
        toast.success(response.data.message);
        setSubmitting(false);
        setSuccessModalOpen(true);
      } else {
        toast.error(response?.data.message || 'Please Try Again');
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, values, setFieldValue, setFieldError, isSubmitting, handleSubmit, getFieldProps, resetForm } = formik;

  // Handle document upload
  const handleDocUpload = async (id, file) => {
    const data = { file: id === 'aadhar_upload' ? 'Aadhaar Card' : 'PAN Card', data: file };
    const response = await Post(apiURL + '/api/v1/users/uploaddoc', { ...data }, navigate);
    if (response && response.data && response.data.status) {
      setUpload((prev) => ({ ...prev, [id]: response?.data?.data?.data }));
      toast.success(`${data.file} Uploaded Successfully`);
      setFieldValue(id, response?.data?.data?.id);
    } else {
      toast.error(response?.data?.message || 'Please Try Again');
    }
  };

  const handleFiles = async (e) => {
    const { id, files } = e.target;
    if ((files[0]['size'] / 1024) <= 2000) {
      const base64 = await Base64File(files[0]);
      const file = { name: files[0]['name'], size: files[0]['size'], base64: base64 };
      handleDocUpload(id, file);
    } else {
      toast.error('Please Upload File less than 2 MB');
    }
  };

  // OTP Verification Functions
  const handleOtpOpen = async (type) => {
    const response = await Post(apiURL + `/api/v1/users/registration/_sent_otp_registration`, { entity: type === 'email' ? values.email : values.phone });
    if (response && response.data && response.data.status) {
      toast.success('OTP sent successfully');
      setOtpType(type);
      setOtpModalOpen(true);
    } else {
      toast.error('Failed to send OTP. Please try again.');
    }
  };

  const handleOtpClose = () => {
    setOtpModalOpen(false);
    setOtpValue('');
  };

  const handleOtpSubmit = async () => {
    const response = await Post(apiURL + `/api/v1/users/registration/_verify_otp_registration`, { entity: otpType === 'email' ? values.email : values.phone, otp: otpValue });
    if (response && response.data && response.data.status) {
      toast.success('OTP Verified Successfully');
      setOtpModalOpen(false);
      setOtpValue('');
      if (otpType === 'email') {
        setIsEmailVerified(true);
        setFieldError('email', ''); // Clear email error on successful verification
      } else {
        setIsPhoneVerified(true);
        setFieldError('phone', ''); // Clear phone error on successful verification
      }
    } else {
      toast.error('Invalid OTP. Please try again.');
    }
  };

  // KYC Verification Functions
  const handlePANVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, pan: true }));
      setVerificationType('PAN');
      setVerificationModalOpen(true);
      
      const panData = {
        pan_number: values.pan_number,
        date_of_birth: values.pan_date_of_birth,
        full_name: values.first_name+' '+values.last_name
      };

      const response = await KYCService.verifyPAN(panData,qr);
      
      if (response?.data?.status) {
        handleKYCSuccess('PAN verification submitted successfully');
        setFieldValue('pan_verified', true);
        setKycStatus(prev => ({ ...prev, pan: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'PAN verification');
    } finally {
      setKycLoading(prev => ({ ...prev, pan: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleAadhaarVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, aadhaar: true }));
      setVerificationType('Aadhaar');
      setVerificationModalOpen(true);
      
      const aadhaarData = {
        aadhaar_number: values.aadhar_number,
        date_of_birth: values.pan_date_of_birth,
        document_image: upload.aadhar_upload?.base64 || null
      };

      const response = await KYCService.verifyAadhaar(aadhaarData,qr);
      
      if (response?.data?.status) {
        handleKYCSuccess('Aadhaar verification submitted successfully');
        setFieldValue('aadhaar_verified', true);
        setKycStatus(prev => ({ ...prev, aadhaar: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Aadhaar verification');
    } finally {
      setKycLoading(prev => ({ ...prev, aadhaar: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleBankVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, bank: true }));
      setVerificationType('Bank Account');
      setVerificationModalOpen(true);
      
      const bankData = {
        account_number: values.account_number,
        ifsc_code: values.bank_ifsc_code,
        account_holder_name: values.bank_account_holder_name
      };

      const response = await KYCService.verifyBank(bankData,qr);
      
      if (response?.data?.status) {
        handleKYCSuccess('Bank verification submitted successfully');
        setFieldValue('bank_verified', true);
        setKycStatus(prev => ({ ...prev, bank: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Bank verification');
    } finally {
      setKycLoading(prev => ({ ...prev, bank: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleChangeCheck = (e) => {
    setChecked(e.target.checked);
  };

  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
    resetForm();
    setChecked(false);
    setUpload({ aadhar_upload: '', pan_upload: '' });
    setKycStatus({ pan: null, aadhaar: null, bank: null });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return <VerifiedUserIcon color="success" />;
      case KYC_STATUS.PROCESSING:
        return <PendingIcon color="warning" />;
      case KYC_STATUS.REJECTED:
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };



  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Logo />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                 Procurement Registration
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Complete your KYC verification to become a procurement agent
                </Typography>
                
                {/* Progress Indicator */}
                <Box sx={{ mb: 4 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0)) * 33.33}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                      }
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    KYC Progress: {((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0))}/3 completed
                  </Typography>
                </Box>
              </Box>

            {/* Section 1: Personal Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                1. Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    {...getFieldProps("first_name")}
                    error={Boolean(touched.first_name && errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    {...getFieldProps("last_name")}
                    error={Boolean(touched.last_name && errors.last_name)}
                    helperText={touched.last_name && errors.last_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    {...getFieldProps("email")}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isEmailVerified ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOtpOpen("email")}
                            >
                              Verify
                            </Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    {...getFieldProps("password")}
                    error={Boolean(touched.password && errors.password)}
                    helperText={
                      (touched.password && errors.password) ||
                      "Enter at least 7 characters"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    {...getFieldProps("phone")}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isPhoneVerified ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOtpOpen("phone")}
                            >
                              Verify
                            </Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Aadhaar Number"
                    {...getFieldProps("aadhar_number")}
                    error={Boolean(touched.aadhar_number && errors.aadhar_number)}
                    helperText={touched.aadhar_number && errors.aadhar_number}
                  />
                </Grid> */}
              </Grid>
            </Box>

            {/* Section 2: PAN Verification */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                2. PAN Card Verification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN Number"
                    {...getFieldProps("pan_number")}
                    error={Boolean(touched.pan_number && errors.pan_number)}
                    helperText={touched.pan_number && errors.pan_number}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    disabled={values.pan_verified}
                    InputProps={{
                      endAdornment: values.pan_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    InputLabelProps={{ shrink: true }}
                    {...getFieldProps("pan_date_of_birth")}
                    error={Boolean(touched.pan_date_of_birth && errors.pan_date_of_birth)}
                    helperText={touched.pan_date_of_birth && errors.pan_date_of_birth}
                    disabled={values.pan_verified}
                    InputProps={{
                      endAdornment: values.pan_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={2}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload PAN Card
                      <VisuallyHiddenInput
                        id="pan_upload"
                        type="file"
                        onChange={(e) => handleFiles(e)}
                      />
                    </Button>
                    <Stack direction={"row"} spacing={1}>
                      <Typography sx={{ display: "flex", alignItems: "center" }}>
                        {upload.pan_upload?.["name"]
                          ? upload.pan_upload?.["name"]
                          : "No File Chosen"}
                      </Typography>
                      {upload.pan_upload?.["name"] ? (
                        <IconButton
                          component={"a"}
                          href={dataURItoBlob(upload.pan_upload?.["base64"])}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <LaunchIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handlePANVerification}
                  loading={kycLoading.pan}
                  disabled={!values.pan_number || !values.pan_date_of_birth || !upload.pan_upload?.base64 || values.pan_verified}
                  startIcon={getStatusIcon(kycStatus.pan)}
                  sx={{ mr: 2 }}
                >
                  {kycLoading.pan ? 'Verifying...' : values.pan_verified ? 'Verified' : 'Verify PAN'}
                </LoadingButton>
              </Box>
            </Box>

            {/* Section 3: Aadhaar Verification */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                3. Aadhaar Card Verification
              </Typography>
              <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Aadhaar Number"
                    {...getFieldProps("aadhar_number")}
                    error={Boolean(touched.aadhar_number && errors.aadhar_number)}
                    helperText={touched.aadhar_number && errors.aadhar_number}
                    disabled={values.aadhaar_verified}
                    InputProps={{
                      endAdornment: values.aadhaar_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Aadhaar Card
                      <VisuallyHiddenInput
                        id="aadhar_upload"
                        type="file"
                        onChange={(e) => handleFiles(e)}
                      />
                    </Button>
                    <Stack direction={"row"} spacing={1}>
                      <Typography sx={{ display: "flex", alignItems: "center" }}>
                        {upload.aadhar_upload?.["name"]
                          ? upload.aadhar_upload?.["name"]
                          : "No File Chosen"}
                      </Typography>
                      {upload.aadhar_upload?.["name"] ? (
                        <IconButton
                          component={"a"}
                          href={dataURItoBlob(upload.aadhar_upload?.["base64"])}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <LaunchIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleAadhaarVerification}
                  loading={kycLoading.aadhaar}
                  disabled={!values.aadhar_number || !upload.aadhar_upload?.base64 || values.aadhaar_verified}
                  startIcon={getStatusIcon(kycStatus.aadhaar)}
                  sx={{ mr: 2 }}
                >
                  {kycLoading.aadhaar ? 'Verifying...' : values.aadhaar_verified ? 'Verified' : 'Verify Aadhaar'}
                </LoadingButton>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 4: Bank Verification */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                4. Bank Account Verification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    {...getFieldProps("bank_name")}
                    error={Boolean(touched.bank_name && errors.bank_name)}
                    helperText={touched.bank_name && errors.bank_name}
                    disabled={values.bank_verified}
                    InputProps={{
                      endAdornment: values.bank_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    {...getFieldProps("account_number")}
                    error={Boolean(touched.account_number && errors.account_number)}
                    helperText={touched.account_number && errors.account_number}
                    disabled={values.bank_verified}
                    InputProps={{
                      endAdornment: values.bank_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="IFSC Code"
                    {...getFieldProps("bank_ifsc_code")}
                    error={Boolean(touched.bank_ifsc_code && errors.bank_ifsc_code)}
                    helperText={touched.bank_ifsc_code && errors.bank_ifsc_code}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    disabled={values.bank_verified}
                    InputProps={{
                      endAdornment: values.bank_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Holder Name"
                    {...getFieldProps("bank_account_holder_name")}
                    error={Boolean(touched.bank_account_holder_name && errors.bank_account_holder_name)}
                    helperText={touched.bank_account_holder_name && errors.bank_account_holder_name}
                    disabled={values.bank_verified}
                    InputProps={{
                      endAdornment: values.bank_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bank Address"
                    {...getFieldProps("bank_address")}
                    error={Boolean(touched.bank_address && errors.bank_address)}
                    helperText={touched.bank_address && errors.bank_address}
                    disabled={values.bank_verified}
                    InputProps={{
                      endAdornment: values.bank_verified ? (
                        <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleBankVerification}
                  loading={kycLoading.bank}
                  disabled={!values.bank_name || !values.account_number || !values.bank_ifsc_code || !values.bank_account_holder_name || values.bank_verified}
                  startIcon={getStatusIcon(kycStatus.bank)}
                  sx={{ mr: 2 }}
                >
                  {kycLoading.bank ? 'Verifying...' : values.bank_verified ? 'Verified' : 'Verify Bank'}
                </LoadingButton>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 5: Additional Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                5. Additional Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Personal Address"
                    {...getFieldProps("address")}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    {...getFieldProps("city")}
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    {...getFieldProps("state")}
                    error={Boolean(touched.state && errors.state)}
                    helperText={touched.state && errors.state}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    {...getFieldProps("country")}
                    error={Boolean(touched.country && errors.country)}
                    helperText={touched.country && errors.country}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    {...getFieldProps("pincode")}
                    error={Boolean(touched.pincode && errors.pincode)}
                    helperText={touched.pincode && errors.pincode}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Nominee Name"
                    {...getFieldProps("nominee_name")}
                    error={Boolean(touched.nominee_name && errors.nominee_name)}
                    helperText={touched.nominee_name && errors.nominee_name}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Nominee Age"
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    {...getFieldProps("nominee_age")}
                    error={Boolean(touched.nominee_age && errors.nominee_age)}
                    helperText={touched.nominee_age && errors.nominee_age}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Nominee Relation"
                    {...getFieldProps("nominee_relation")}
                    error={Boolean(touched.nominee_relation && errors.nominee_relation)}
                    helperText={touched.nominee_relation && errors.nominee_relation}
                  />
                </Grid>
              </Grid>

              {/* KYC Status Summary */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  KYC Verification Status
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip
                    label={`PAN: ${values.pan_verified ? 'VERIFIED' : 'PENDING'}`}
                    color={values.pan_verified ? 'success' : 'warning'}
                    icon={getStatusIcon(values.pan_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                  />
                  <Chip
                    label={`Aadhaar: ${values.aadhaar_verified ? 'VERIFIED' : 'PENDING'}`}
                    color={values.aadhaar_verified ? 'success' : 'warning'}
                    icon={getStatusIcon(values.aadhaar_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                  />
                  <Chip
                    label={`Bank: ${values.bank_verified ? 'VERIFIED' : 'PENDING'}`}
                    color={values.bank_verified ? 'success' : 'warning'}
                    icon={getStatusIcon(values.bank_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                  />
                </Stack>
              </Box>
            </Box>

            {/* Terms and Conditions */}
            <Stack ml={"20px"} spacing={1} direction={"row"} sx={{ mt: 4 }}>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox checked={checked} onChange={handleChangeCheck} />
                }
                label=""
                labelPlacement="end"
              />
              <Typography
                align="center"
                justifyContent={"center"}
                display={"flex"}
                alignItems={"center"}
              >
                I Accept the{" "}
                <a
                  href={require("../../../img/terms and conditions.pdf")}
                  target="_blank"
                  rel="noreferrer"
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                >
                  Terms and Conditions
                </a>
              </Typography>
            </Stack>
          </CardContent>

          <CardActions sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              href="#/dashboard/procurementUser"
              variant="contained"
              sx={{
                backgroundColor: "#3f51b5",
                "&:hover": { color: "#ffff", backgroundColor: "#3f51b5" },
              }}
            >
              Back
            </Button>
            
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!checked || !values.pan_verified || !values.aadhaar_verified || !values.bank_verified}
              sx={{
                backgroundColor: "#3f51b5",
                "&:hover": { color: "#ffff", backgroundColor: "#3f51b5" },
              }}
            >
              Submit
            </LoadingButton>
          </CardActions>
          </StyledCard>

        {/* Verification Modal */}
        <Modal open={verificationModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              textAlign: 'center'
            }}
          >
            <CircularProgress 
              size={64} 
              sx={{ 
                color: 'primary.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Verifying {verificationType}...
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Please wait while we verify your {verificationType.toLowerCase()} information. This may take a few moments.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: 1,
              mt: 2
            }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Processing...
              </Typography>
            </Box>
          </Box>
        </Modal>

        {/* Success Modal */}
        <Modal open={successModalOpen} onClose={handleSuccessClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              textAlign: 'center'
            }}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: 64, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Procurement Agent Created Successfully!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Your procurement agent application has been submitted and is under review.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleSuccessClose}
                sx={{ minWidth: 100 }}
              >
                Create Another
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleSuccessClose();
                  navigate('/dashboard/procurementUser');
                }}
                sx={{ minWidth: 100 }}
              >
                Go to Dashboard
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* OTP Verification Modal */}
        <Modal open={otpModalOpen} onClose={handleOtpClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" align="center" gutterBottom>
              Enter OTP for {otpType === "email" ? "Email" : "Phone"}
            </Typography>
            <TextField
              fullWidth
              label="OTP"
              type="text"
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              error={Boolean(!otpValue)}
              helperText={!otpValue ? "OTP is required" : ""}
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleOtpClose}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleOtpSubmit}
                disabled={!otpValue}
                sx={{ minWidth: 100 }}
              >
                Verify OTP
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Form>
    </FormikProvider>
    </Container>
  );
};

export default ProcurementCreate;
